import { selectCanvasElements, updateCanvasSelection } from '@/utils/elementsUtils';

// Same snapshot the transform start takes: a deep clone would park a copy of the whole scene in
//  the undo stack for the rest of the session
const childSnapshots = (element) => window.fabric.util.saveObjectTransform(element).objects;

const buildTransformFunc = (canvas, object, transformCallback, ...tranformParams) => {
    const canvasObjs = canvas.getObjects();
    canvas.discardActiveObject();

    if (object.isType('activeSelection')) {
        const selectionObjects = object.getObjects().map((obj) => {
            return obj.isCloned ? canvasObjs.find((o) => o.uuid === obj.uuid) : obj;
        });
        selectCanvasElements(selectionObjects, canvas);
        object = canvas.getActiveObject();
        transformCallback(object, ...tranformParams);
        // update the selection box
        updateCanvasSelection(canvas);
        canvas.fire('object:transform:restored', { target: object });
        return;
    } else if (object.isCloned) {
        object = canvasObjs.find((obj) => obj.uuid === object.uuid);
    }

    transformCallback(object, ...tranformParams);
    if (object.pageEpsId !== canvas.getFirstActivePageEpsId()) {
        canvas.setActivePageEps(object.pageEpsId, false);
    }
    canvas.discardActiveObject();
    canvas.setActiveObject(object);
    canvas.renderAll();
    // Set in place, so none of the moving/modified events that elements following this one listen to fire
    canvas.fire('object:transform:restored', { target: object });
};

// TODO: Replace these parameters with an object
const dragElement = (object, top, left, width, height, horizontalDiameter, verticalDiameter) => {
    object.set({ top, left, width, height, horizontalDiameter, verticalDiameter });
    object.setCoords();
};

const dragInnerElements = (object, children) => {
    object.getObjects().forEach((element, idx) => {
        const prevState = children[idx];
        dragElement(
            element,
            prevState.top,
            prevState.left,
            prevState.width,
            prevState.height,
            prevState.horizontalDiameter,
            prevState.verticalDiameter
        );
    });
};

const rotate = (object, angle, childObjects) => {
    if (!object.isType('activeSelection')) {
        object.set({ angle });
        object.setCoords();
        return;
    }

    // manually set previous position for each selection child
    object.getObjects().forEach((element, idx) => {
        const prevState = childObjects[idx];
        element.set({
            angle: prevState.angle,
            top: prevState.top,
            left: prevState.left
        });
    });

    object.setCoords();
};

const scaleElement = (object, width, height, top, left, childObjects) => {
    if (object.setScale) {
        object.setScale.apply(object, [width, height, object.isType('image-placeholder'), false]);
    } else if (object.isType('path-itext') && object.setRadius) {
        /**
         * in case of path-itext width and height are
         * horizontal and vertical diameter respectively
         */
        object.setRadius.apply(object, [width / 2, height / 2]);
    } else if (object.isType('activeSelection')) {
        object.set({ width, height });
        // manually set previous size for each selection child
        for (const [idx, child] of object.getObjects().entries()) {
            const prevState = childObjects[idx];
            scaleElement(
                child,
                prevState.horizontalDiameter || prevState.width,
                prevState.verticalDiameter || prevState.height,
                prevState.top,
                prevState.left
            );
        }
    }
    object.set({ top, left });
    object.setCoords();
};

export const addEventListenersForUndoRedo = (fabricObject) => {
    fabricObject.on({
        modified(event) {
            const canvas = this.canvas;
            if (!event?.transform || !canvas?.undoManager) return;

            const { transform, target: element } = event;
            const prevState = transform?.original;
            const params = { undo: [], redo: [] };
            const canvasElement = element;

            switch (transform?.action) {
                case 'drag': {
                    params.redo = [
                        canvas,
                        canvasElement,
                        dragElement,
                        element.top,
                        element.left,
                        element.width,
                        element.height,
                        element.horizontalDiameter,
                        element.verticalDiameter
                    ];
                    params.undo = [
                        canvas,
                        canvasElement,
                        dragElement,
                        prevState.top,
                        prevState.left,
                        prevState.width,
                        prevState.height,
                        prevState.horizontalDiameter,
                        prevState.verticalDiameter
                    ];
                    break;
                }
                case 'dragInsideSelection': {
                    params.redo = [canvas, canvasElement, dragInnerElements, childSnapshots(canvasElement)];
                    params.undo = [canvas, canvasElement, dragInnerElements, prevState.objects];
                    break;
                }
                case 'rotate': {
                    params.redo = [canvas, canvasElement, rotate, element.angle, childSnapshots(canvasElement)];
                    params.undo = [canvas, canvasElement, rotate, prevState.angle, prevState.objects];
                    break;
                }
                case 'scale':
                case 'scaleX':
                case 'scaleY': {
                    /**
                     * manually get the coordinates for activeSelection objects,
                     * since the event target doesn't return the current values
                     */
                    const bounds = element.isType('activeSelection') ? element.getBoundingRect(true) : element;
                    params.redo = [
                        canvas,
                        canvasElement,
                        scaleElement,
                        element.horizontalDiameter || bounds.width,
                        element.verticalDiameter || bounds.height,
                        bounds.top,
                        bounds.left,
                        childSnapshots(canvasElement)
                    ];
                    params.undo = [
                        canvas,
                        canvasElement,
                        scaleElement,
                        prevState.horizontalDiameter || prevState.width,
                        prevState.verticalDiameter || prevState.height,
                        prevState.top,
                        prevState.left,
                        prevState.objects
                    ];
                    break;
                }
                case 'perspective-coords': {
                    // TODO: Implement undo/redo for perspective-coords
                    return;
                }
                default:
                    console.warn(`Performed action (${transform?.action}) not handle by undo/redo`);
                    return;
            }

            canvas.undoManager.add(buildTransformFunc, params.redo, buildTransformFunc, params.undo);
        }
    });
};
